/**
 * Public employer signup page.
 *
 * We block obvious free-mail domains (server-side check too) so recruiters
 * arrive with a work email tied to their company. Result: a "pending"
 * account that an admin verifies inside /admin/employers.
 */
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Briefcase, ShieldCheck, ArrowRight, Building2, FilePlus, Search } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Kept identical to the backend list so the client can surface the message
// before firing off a wasted POST.
const _FREE_MAIL = new Set([
  "gmail.com", "yahoo.com", "hotmail.com", "outlook.com", "aol.com",
  "icloud.com", "protonmail.com", "proton.me", "mail.com", "gmx.com",
]);

export default function EmployerSignup() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    email: "", password: "", first_name: "", last_name: "",
    company: "", linkedin_url: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const update = (k) => (e) => setForm((prev) => ({ ...prev, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const domain = (form.email.split("@")[1] || "").toLowerCase();
    if (_FREE_MAIL.has(domain)) {
      setError("Please sign up with your work email — free-mail addresses aren't accepted.");
      return;
    }
    if (form.password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${BACKEND_URL}/api/employer/signup`, form,
        { withCredentials: true });
      // Immediately log them in — the pending page will explain what happens next.
      await axios.post(`${BACKEND_URL}/api/employer/login`,
        { email: form.email, password: form.password },
        { withCredentials: true });
      navigate("/employer/pending", { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || "Signup failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen py-10 px-4"
         style={{ background: "linear-gradient(180deg, #FFFBEB 0%, #FEF3C7 100%)" }}>
      <div className="max-w-lg mx-auto">
        <div className="mb-6 text-center">
          <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full"
               style={{ background: "#0F1B2D", color: "#FFC145" }}>
            <Briefcase size={14} />
            <span className="font-mono-rm text-[10px] uppercase tracking-widest" style={{ fontWeight: 700 }}>
              Resmo · For Employers
            </span>
          </div>
          <h1 className="font-display uppercase leading-tight"
              style={{ fontSize: "clamp(1.6rem, 3vw, 2.2rem)", color: "#0F1B2D", letterSpacing: "-0.02em" }}>
            Discover candidates on Resmo
          </h1>
          <p className="mt-2 text-sm leading-relaxed max-w-md mx-auto" style={{ color: "#334155" }}>
            Verified employer accounts unlock the Talent Directory — search by
            skills, location, and years of experience. Sign up with your work
            email; we&apos;ll verify your account and email you when you&apos;re approved.
          </p>
        </div>

        {/* How Resmo Talent Directory works — 3-step trust strip */}
        <div className="rounded-md p-4 mb-5"
             style={{ background: "white", border: "1.5px solid #0F1B2D", boxShadow: "3px 3px 0 0 #FFC145" }}
             data-testid="employer-signup-how-it-works">
          <div className="font-mono-rm text-[10px] uppercase tracking-widest mb-3 text-center"
               style={{ color: "#FF6A00", fontWeight: 700, letterSpacing: "0.12em" }}>
            · How Resmo Talent Directory works
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <HowStep num="1" icon={FilePlus} title="Sign up"
                     body="Work email + your company. Takes 30 seconds." />
            <HowStep num="2" icon={ShieldCheck} title="Get verified"
                     body="An admin checks your domain + LinkedIn. Usually within one business day." />
            <HowStep num="3" icon={Search} title="Search candidates"
                     body="Filter by role, skills, location, years — and contact opted-in talent." />
          </div>
        </div>

        <form onSubmit={submit}
              className="rounded-md p-5 md:p-6 space-y-3"
              style={{ background: "white", border: "2px solid #0F1B2D", boxShadow: "4px 4px 0 0 #FFC145" }}
              data-testid="employer-signup-form">
          <Field label="Work email" required
                 value={form.email} onChange={update("email")}
                 type="email" placeholder="you@company.com"
                 testid="employer-signup-email" />
          <Field label="Password" required
                 value={form.password} onChange={update("password")}
                 type="password" placeholder="at least 8 characters"
                 testid="employer-signup-password" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="First name" required
                   value={form.first_name} onChange={update("first_name")}
                   testid="employer-signup-first" />
            <Field label="Last name" required
                   value={form.last_name} onChange={update("last_name")}
                   testid="employer-signup-last" />
          </div>
          <Field label="Company" required icon={Building2}
                 value={form.company} onChange={update("company")}
                 placeholder="Acme Corp"
                 testid="employer-signup-company" />
          <Field label="Your LinkedIn URL (optional but speeds up verification)"
                 value={form.linkedin_url} onChange={update("linkedin_url")}
                 placeholder="https://linkedin.com/in/…"
                 testid="employer-signup-linkedin" />

          {error && (
            <div className="text-[12px] rounded px-3 py-2"
                 style={{ background: "#FEE2E2", color: "#B91C1C", border: "1px solid #FCA5A5" }}
                 data-testid="employer-signup-error">
              {error}
            </div>
          )}
          <button type="submit" disabled={busy}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-md px-4 py-2.5 font-display uppercase text-[13px] tracking-widest"
                  style={{
                    background: busy ? "#94A3B8" : "#FF6A00", color: "white",
                    border: "2px solid #0F1B2D",
                    boxShadow: "3px 3px 0 0 #0F1B2D",
                    fontWeight: 700, cursor: busy ? "wait" : "pointer",
                  }}
                  data-testid="employer-signup-submit">
            {busy ? "Creating…" : (<><ShieldCheck size={14} /> Create employer account <ArrowRight size={14} /></>)}
          </button>
          <div className="text-center mt-1 text-[11px]" style={{ color: "#64748B" }}>
            Already have an account?{" "}
            <Link to="/employer/login" className="underline" style={{ color: "#0F1B2D", fontWeight: 600 }}
                  data-testid="employer-signup-goto-login">
              Sign in
            </Link>
          </div>
        </form>
        <div className="mt-5 text-center">
          <Link to="/" className="font-mono-rm text-[10px] uppercase tracking-widest"
                style={{ color: "#94A3B8" }} data-testid="employer-signup-back-to-candidate">
            · Are you a candidate? Back to Resmo →
          </Link>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder = "", required = false, testid, icon: Icon }) {
  return (
    <label className="block">
      <div className="font-mono-rm text-[10px] uppercase tracking-widest mb-1"
           style={{ color: "#475569", fontWeight: 700 }}>
        · {label}
      </div>
      <div className="relative">
        {Icon && (
          <Icon size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2"
                style={{ color: "#94A3B8" }} aria-hidden />
        )}
        <input
          type={type} value={value} onChange={onChange}
          placeholder={placeholder} required={required}
          className="w-full rounded px-3 py-2 text-[13px]"
          style={{
            border: "1.5px solid #CBD5E1", background: "#F8FAFC",
            paddingLeft: Icon ? "2rem" : undefined,
          }}
          data-testid={testid}
        />
      </div>
    </label>
  );
}

function HowStep({ num, icon: Icon, title, body }) {
  return (
    <div className="flex items-start gap-2.5 text-left"
         data-testid={`employer-how-step-${num}`}>
      <div className="flex-shrink-0 inline-flex items-center justify-center rounded-md"
           style={{
             width: 34, height: 34, background: "#FEF3C7",
             border: "1.5px solid #0F1B2D",
             boxShadow: "1.5px 1.5px 0 0 #0F1B2D",
           }}>
        <Icon size={15} style={{ color: "#0F1B2D" }} />
      </div>
      <div className="min-w-0">
        <div className="flex items-baseline gap-1.5">
          <span className="font-mono-rm text-[10px] uppercase tracking-widest"
                style={{ color: "#FF6A00", fontWeight: 700 }}>· Step {num}</span>
        </div>
        <div className="font-display uppercase leading-tight"
             style={{ fontSize: "0.85rem", color: "#0F1B2D", letterSpacing: "-0.005em" }}>
          {title}
        </div>
        <div className="text-[11px] leading-snug mt-0.5" style={{ color: "#475569" }}>
          {body}
        </div>
      </div>
    </div>
  );
}

